import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Search,
  Plus,
  Edit,
  Camera,
  DollarSign,
  Building,
  Phone,
  Mail,
  Calendar as CalendarIcon,
  CheckCircle,
  XCircle,
  ChevronRight,
  Loader2,
  User,
  CreditCard,
  Briefcase,
  Clock,
  Award,
  MapPin,
  Truck,
  ChevronLeft,
  Eye,
  Wallet,
  TrendingUp,
  TrendingDown,
  FileText,
  Filter,
  RotateCcw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import SelfieCapture from '@/components/SelfieCapture';
import EmployeeIncentiveSettingsDialog from '@/components/EmployeeIncentiveSettingsDialog';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import { DateInput, TimeInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EmployeeManagementPage = () => {
  const { user } = useAuth();
  // Currency comes from company settings (Settings → Currency). The
  // page used to hardcode AED in 12 places — broken for non-UAE
  // tenants. Use this everywhere a currency label is shown.
  const { currency } = useSettings();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [incentiveSettingsEmployee, setIncentiveSettingsEmployee] = useState(null);
  const [incentiveSettingsOpen, setIncentiveSettingsOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [faceDialogOpen, setFaceDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Attendance Calendar state
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [calendarEmployee, setCalendarEmployee] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  
  // Manual Attendance Edit state
  const [editAttendanceDialogOpen, setEditAttendanceDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [attendanceFormData, setAttendanceFormData] = useState({
    status: 'present',
    check_in_time: '',
    check_out_time: '',
    notes: ''
  });
  const [savingAttendance, setSavingAttendance] = useState(false);
  
  // Locations and Shifts for dropdown
  const [locations, setLocations] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [hrConfig, setHrConfig] = useState(null);
  
  // Employee Ledger state
  const [ledgerDialogOpen, setLedgerDialogOpen] = useState(false);
  const [ledgerEmployee, setLedgerEmployee] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [ledgerFilter, setLedgerFilter] = useState('all');
  const [ledgerDateRange, setLedgerDateRange] = useState({ start: '', end: '' });
  const [addAdjustmentDialogOpen, setAddAdjustmentDialogOpen] = useState(false);
  const [adjustmentForm, setAdjustmentForm] = useState({
    entry_type: 'incentive',
    amount: '',
    description: ''
  });
  const [savingAdjustment, setSavingAdjustment] = useState(false);
  
  // Holidays for calendar
  const [calendarHolidays, setCalendarHolidays] = useState([]);
  
  // Form data for editing
  const [formData, setFormData] = useState({
    monthly_salary: '',
    daily_salary: '',
    hourly_rate: '',
    overtime_rate: '1.5',
    undertime_rate: '1.0',
    housing_allowance: '',
    transport_allowance: '',
    food_allowance: '',
    other_allowances: '',
    bank_name: '',
    bank_account_number: '',
    iban: '',
    employee_code: '',
    department: '',
    designation: '',
    joining_date: '',
    contract_end_date: '',
    employment_type: 'full_time',
    paid_leave_balance: '30',
    sick_leave_balance: '10',
    // New attendance assignment fields
    work_type: 'office',
    assigned_location_id: '',
    assigned_shift_id: ''
  });

  // Get unique departments
  const departments = [...new Set(employees.map(e => e.hr_data?.department).filter(Boolean))];

  const fetchEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (departmentFilter !== 'all') params.append('department', departmentFilter);
      
      const response = await axios.get(`${API_BASE_URL}/hr/employees?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.employees);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, departmentFilter]);

  const fetchLocationsAndShifts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [locationsRes, shiftsRes, configRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/hr/locations`, { headers }),
        axios.get(`${API_BASE_URL}/hr/shifts`, { headers }),
        axios.get(`${API_BASE_URL}/hr/config`, { headers })
      ]);
      
      setLocations(locationsRes.data.locations || []);
      setShifts(shiftsRes.data.shifts || []);
      setHrConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching locations/shifts:', error);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
    fetchLocationsAndShifts();
  }, [fetchEmployees, fetchLocationsAndShifts]);

  // Format time in company timezone
  const formatTimeInCompanyTz = (timeString) => {
    if (!timeString) return '--:--';
    const companyTimezone = hrConfig?.timezone_name || 'Asia/Dubai';
    try {
      return new Date(timeString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: companyTimezone
      });
    } catch {
      return new Date(timeString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  const handleEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    const hr = employee.hr_data || {};
    setFormData({
      monthly_salary: hr.monthly_salary != null ? hr.monthly_salary.toString() : '',
      daily_salary: hr.daily_salary != null ? hr.daily_salary.toString() : '',
      hourly_rate: hr.hourly_rate != null ? hr.hourly_rate.toString() : '',
      overtime_rate: hr.overtime_rate != null ? hr.overtime_rate.toString() : '',
      undertime_rate: hr.undertime_rate != null ? hr.undertime_rate.toString() : '1.0',
      housing_allowance: hr.housing_allowance != null ? hr.housing_allowance.toString() : '',
      transport_allowance: hr.transport_allowance != null ? hr.transport_allowance.toString() : '',
      food_allowance: hr.food_allowance != null ? hr.food_allowance.toString() : '',
      other_allowances: hr.other_allowances != null ? hr.other_allowances.toString() : '',
      bank_name: hr.bank_name || '',
      bank_account_number: hr.bank_account_number || '',
      iban: hr.iban || '',
      employee_code: hr.employee_code || '',
      department: hr.department || '',
      designation: hr.designation || '',
      joining_date: hr.joining_date || '',
      contract_end_date: hr.contract_end_date || '',
      employment_type: hr.employment_type || 'full_time',
      paid_leave_balance: hr.paid_leave_balance != null ? hr.paid_leave_balance.toString() : '',
      sick_leave_balance: hr.sick_leave_balance != null ? hr.sick_leave_balance.toString() : '',
      // Attendance assignment fields
      work_type: hr.work_type || 'office',
      assigned_location_id: hr.assigned_location_id || '',
      assigned_shift_id: hr.assigned_shift_id || ''
    });
    setEditDialogOpen(true);
  };

  // Open attendance calendar for an employee
  const handleViewAttendance = async (employee) => {
    setCalendarEmployee(employee);
    setCalendarMonth(new Date());
    setCalendarDialogOpen(true);
    await fetchAttendanceRecords(employee.id, new Date());
  };

  // Fetch attendance records for a specific month
  const fetchAttendanceRecords = async (userId, month) => {
    setLoadingAttendance(true);
    try {
      const token = localStorage.getItem('token');
      const startDate = new Date(month.getFullYear(), month.getMonth(), 1);
      const endDate = new Date(month.getFullYear(), month.getMonth() + 1, 0);

      // Format YYYY-MM-DD using LOCAL components — not `.toISOString()`.
      // `.toISOString()` converts to UTC, which silently rolls the
      // last day of the month back to the previous day for any user
      // east of UTC (e.g. UAE: 30 Apr 00:00 +0400 → 29 Apr 20:00 UTC).
      // The API then filters out 30 Apr's record because the requested
      // `end_date` is `2026-04-29`. This was the merchant's
      // "added record for 30th but calendar shows No Record" bug.
      const fmt = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };

      // Fetch attendance records
      const response = await axios.get(`${API_BASE_URL}/hr/attendance/records`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          user_id: userId,
          start_date: fmt(startDate),
          end_date: fmt(endDate)
        }
      });
      setAttendanceRecords(response.data.records || []);
      
      // Fetch holidays for the same month
      try {
        const holidayResponse = await axios.get(`${API_BASE_URL}/hr/holidays`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            start_date: fmt(startDate),
            end_date: fmt(endDate)
          }
        });
        setCalendarHolidays(holidayResponse.data.holidays || []);
      } catch (holidayError) {
        console.error('Error fetching holidays:', holidayError);
        setCalendarHolidays([]);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      toast.error('Failed to load attendance records');
    } finally {
      setLoadingAttendance(false);
    }
  };

  // Navigate calendar months
  const handlePrevMonth = () => {
    const newMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1);
    setCalendarMonth(newMonth);
    if (calendarEmployee) {
      fetchAttendanceRecords(calendarEmployee.id, newMonth);
    }
  };

  const handleNextMonth = () => {
    const newMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1);
    setCalendarMonth(newMonth);
    if (calendarEmployee) {
      fetchAttendanceRecords(calendarEmployee.id, newMonth);
    }
  };

  // Handle clicking on a calendar day to edit attendance
  const handleDayClick = (day, record) => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    setSelectedDate(dateStr);
    
    // Pre-fill form with existing record data if available
    if (record) {
      // Helper: convert a UTC ISO timestamp → "HH:mm" in the company's
      // timezone. Using getUTCHours() (the old implementation) showed the
      // raw UTC hour, which differed from the calendar cell (which shifts
      // to company TZ) — e.g. stored 05:57 UTC rendered as "05:57 AM" in
      // the edit modal but "09:57 AM" in the calendar for a Dubai (UTC+4)
      // tenant. We now format both views in the same timezone.
      const companyTimezone = hrConfig?.timezone_name || 'Asia/Dubai';
      const toLocalHHMM = (iso) => {
        try {
          const d = new Date(iso);
          if (isNaN(d.getTime())) return '';
          // en-GB 24-hour format with timeZone gives us "HH:mm" or "HH:mm:ss"
          const parts = new Intl.DateTimeFormat('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: companyTimezone,
          }).formatToParts(d);
          const h = parts.find(p => p.type === 'hour')?.value || '00';
          const m = parts.find(p => p.type === 'minute')?.value || '00';
          // Some locales render midnight as "24:xx" — normalize to "00"
          return `${h === '24' ? '00' : h}:${m}`;
        } catch {
          return '';
        }
      };

      const checkInTime = record.check_in_time ? toLocalHHMM(record.check_in_time) : '';
      const checkOutTime = record.check_out_time ? toLocalHHMM(record.check_out_time) : '';

      setAttendanceFormData({
        status: record.status || 'present',
        check_in_time: checkInTime,
        check_out_time: checkOutTime,
        notes: ''
      });
    } else {
      // Default values for new record
      setAttendanceFormData({
        status: 'present',
        check_in_time: '09:00',
        check_out_time: '18:00',
        notes: ''
      });
    }
    
    setEditAttendanceDialogOpen(true);
  };

  // Save manual attendance
  const handleSaveAttendance = async () => {
    if (!calendarEmployee || !selectedDate) return;
    
    setSavingAttendance(true);
    try {
      const token = localStorage.getItem('token');
      
      // Clear times for absent/leave statuses
      const shouldClearTimes = ['absent', 'on_leave', 'week_off'].includes(attendanceFormData.status);
      
      await axios.post(
        `${API_BASE_URL}/hr/attendance/manual`,
        {
          user_id: calendarEmployee.id,
          date: selectedDate,
          status: attendanceFormData.status,
          check_in_time: shouldClearTimes ? null : (attendanceFormData.check_in_time || null),
          check_out_time: shouldClearTimes ? null : (attendanceFormData.check_out_time || null),
          notes: attendanceFormData.notes || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Attendance record saved');
      setEditAttendanceDialogOpen(false);
      
      // Refresh attendance records
      await fetchAttendanceRecords(calendarEmployee.id, calendarMonth);
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error(error.response?.data?.detail || 'Failed to save attendance');
    } finally {
      setSavingAttendance(false);
    }
  };

  // Delete attendance record
  const handleDeleteAttendance = async () => {
    if (!calendarEmployee || !selectedDate) return;
    
    if (!confirm('Are you sure you want to delete this attendance record?')) return;
    
    setSavingAttendance(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/hr/attendance/manual/${calendarEmployee.id}/${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Attendance record deleted');
      setEditAttendanceDialogOpen(false);
      
      // Refresh attendance records
      await fetchAttendanceRecords(calendarEmployee.id, calendarMonth);
    } catch (error) {
      console.error('Error deleting attendance:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete attendance');
    } finally {
      setSavingAttendance(false);
    }
  };

  // ============ Employee Ledger Functions ============
  const handleViewLedger = async (employee) => {
    setLedgerEmployee(employee);
    setLedgerDialogOpen(true);
    setLedgerFilter('all');
    setLedgerDateRange({ start: '', end: '' });
    await fetchEmployeeLedger(employee.id);
  };

  const fetchEmployeeLedger = async (userId, range) => {
    setLoadingLedger(true);
    try {
      const token = localStorage.getItem('token');
      
      // First, sync any missing incentives to the ledger
      await axios.post(`${API_BASE_URL}/hr/ledger/${userId}/sync-incentives`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {}); // Ignore errors from sync, it's optional
      
      const r = range || { start: '', end: '' };
      const params = {};
      if (r.start) params.start_date = r.start;
      if (r.end) params.end_date = r.end;
      // Then fetch the ledger
      const response = await axios.get(`${API_BASE_URL}/hr/ledger/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setLedgerEntries(response.data.entries || []);
      setLedgerSummary({
        total_credits: response.data.total_credits || 0,
        total_debits: response.data.total_debits || 0,
        net_balance: response.data.net_balance || 0
      });
    } catch (error) {
      console.error('Error fetching ledger:', error);
      toast.error('Failed to load employee ledger');
      setLedgerEntries([]);
      setLedgerSummary(null);
    } finally {
      setLoadingLedger(false);
    }
  };

  const handleLedgerDateChange = async (field, value) => {
    const next = { ...ledgerDateRange, [field]: value };
    setLedgerDateRange(next);
    if (ledgerEmployee) await fetchEmployeeLedger(ledgerEmployee.id, next);
  };

  const getFilteredLedgerEntries = () => {
    if (ledgerFilter === 'all') return ledgerEntries;
    return ledgerEntries.filter(entry => entry.entry_type === ledgerFilter);
  };

  const handleAddAdjustment = async () => {
    if (!adjustmentForm.amount || !adjustmentForm.description) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSavingAdjustment(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/hr/ledger/adjustment`,
        {
          user_id: ledgerEmployee.id,
          entry_type: adjustmentForm.entry_type,
          amount: parseFloat(adjustmentForm.amount),
          description: adjustmentForm.description
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Adjustment added successfully');
      setAddAdjustmentDialogOpen(false);
      setAdjustmentForm({ entry_type: 'incentive', amount: '', description: '' });
      await fetchEmployeeLedger(ledgerEmployee.id);
    } catch (error) {
      console.error('Error adding adjustment:', error);
      toast.error(error.response?.data?.detail || 'Failed to add adjustment');
    } finally {
      setSavingAdjustment(false);
    }
  };

  const handleCancelLedgerEntry = async (entryId) => {
    if (!confirm('Are you sure you want to cancel this transaction? It will still be visible but the amount will not be counted.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/hr/ledger/${entryId}/cancel`,
        {
          cancelled_by: user?.id,
          reason: 'Cancelled by admin'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Transaction cancelled');
      await fetchEmployeeLedger(ledgerEmployee.id);
    } catch (error) {
      console.error('Error cancelling entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel transaction');
    }
  };

  const handleRestoreLedgerEntry = async (entryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/hr/ledger/${entryId}/restore`,
        { restored_by: user?.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Transaction restored');
      await fetchEmployeeLedger(ledgerEmployee.id);
    } catch (error) {
      console.error('Error restoring entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore transaction');
    }
  };

  // Calculate ledger summary excluding cancelled entries
  const calculateLedgerSummary = () => {
    const activeEntries = ledgerEntries.filter(e => !e.is_cancelled);
    const total_credits = activeEntries.filter(e => e.amount > 0).reduce((sum, e) => sum + e.amount, 0);
    const total_debits = activeEntries.filter(e => e.amount < 0).reduce((sum, e) => sum + Math.abs(e.amount), 0);
    return {
      total_credits,
      total_debits,
      net_balance: total_credits - total_debits
    };
  };

  const getLedgerEntryStyle = (entryType) => {
    const styles = {
      salary: { bg: 'bg-green-500/10', text: 'text-green-400', label: 'Salary' },
      incentive: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', label: 'Incentive' },
      advance: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Advance Issued' },
      advance_deduction: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Advance Deduction' },
      loan: { bg: 'bg-purple-500/10', text: 'text-purple-400', label: 'Loan Issued' },
      loan_deduction: { bg: 'bg-purple-500/10', text: 'text-purple-400', label: 'Loan Deduction' },
      penalty: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Penalty' },
      leave_deduction: { bg: 'bg-orange-500/10', text: 'text-orange-400', label: 'Leave Deduction' },
      adjustment: { bg: 'bg-gray-500/10', text: 'text-gray-400', label: 'Adjustment' },
      bonus: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Bonus' },
      payment: { bg: 'bg-teal-500/10', text: 'text-teal-400', label: 'Payment' },
      expense_reimbursement: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', label: 'Reimbursement' },
      expense_reimbursement_payment: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', label: 'Reimb. Paid' }
    };
    return styles[entryType] || { bg: 'bg-gray-500/10', text: 'text-gray-400', label: entryType };
  };

  const handleSaveEmployee = async () => {
    if (!selectedEmployee) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build update data - only include fields with actual values
      const updateData = {};
      
      // Numeric fields - only include if user entered a value
      if (formData.monthly_salary !== '') {
        updateData.monthly_salary = parseFloat(formData.monthly_salary) || 0;
      }
      if (formData.daily_salary !== '') {
        updateData.daily_salary = parseFloat(formData.daily_salary) || 0;
      }
      if (formData.hourly_rate !== '') {
        updateData.hourly_rate = parseFloat(formData.hourly_rate) || 0;
      }
      if (formData.overtime_rate !== '') {
        // Allow 0 for overtime_rate (employee gets no overtime pay)
        const parsedRate = parseFloat(formData.overtime_rate);
        updateData.overtime_rate = isNaN(parsedRate) ? 1.5 : parsedRate;
      }
      if (formData.undertime_rate !== '') {
        // Allow 0 for undertime_rate (no deduction for less hours)
        const parsedRate = parseFloat(formData.undertime_rate);
        updateData.undertime_rate = isNaN(parsedRate) ? 1.0 : parsedRate;
      }
      if (formData.housing_allowance !== '') {
        updateData.housing_allowance = parseFloat(formData.housing_allowance) || 0;
      }
      if (formData.transport_allowance !== '') {
        updateData.transport_allowance = parseFloat(formData.transport_allowance) || 0;
      }
      if (formData.food_allowance !== '') {
        updateData.food_allowance = parseFloat(formData.food_allowance) || 0;
      }
      if (formData.other_allowances !== '') {
        updateData.other_allowances = parseFloat(formData.other_allowances) || 0;
      }
      if (formData.paid_leave_balance !== '') {
        updateData.paid_leave_balance = parseFloat(formData.paid_leave_balance);
      }
      if (formData.sick_leave_balance !== '') {
        updateData.sick_leave_balance = parseFloat(formData.sick_leave_balance);
      }
      
      // String fields - only include if user entered a value
      if (formData.bank_name) updateData.bank_name = formData.bank_name;
      if (formData.bank_account_number) updateData.bank_account_number = formData.bank_account_number;
      if (formData.iban) updateData.iban = formData.iban;
      if (formData.employee_code) updateData.employee_code = formData.employee_code;
      if (formData.department) updateData.department = formData.department;
      if (formData.designation) updateData.designation = formData.designation;
      if (formData.joining_date) updateData.joining_date = formData.joining_date;
      if (formData.contract_end_date) updateData.contract_end_date = formData.contract_end_date;
      if (formData.employment_type) updateData.employment_type = formData.employment_type;
      
      // Attendance assignment fields - always include if set
      if (formData.work_type) updateData.work_type = formData.work_type;
      if (formData.assigned_location_id) {
        updateData.assigned_location_id = formData.assigned_location_id;
      } else {
        updateData.assigned_location_id = null;
      }
      if (formData.assigned_shift_id) {
        updateData.assigned_shift_id = formData.assigned_shift_id;
      } else {
        updateData.assigned_shift_id = null;
      }

      await axios.put(
        `${API_BASE_URL}/hr/employees/${selectedEmployee.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Employee updated successfully');
      setEditDialogOpen(false);
      fetchEmployees();
    } catch (error) {
      console.error('Error updating employee:', error);
      toast.error('Failed to update employee');
    } finally {
      setSaving(false);
    }
  };

  const handleRegisterFace = (employee) => {
    setSelectedEmployee(employee);
    setFaceDialogOpen(true);
  };

  const EmployeeCard = ({ employee }) => {
    const hr = employee.hr_data || {};
    const hasSelfie = !!hr.selfie_registered;
    const hasSalary = !!hr.monthly_salary;
    const workType = hr.work_type || 'office';
    
    // Get location and shift names
    const assignedLocation = locations.find(l => l.id === hr.assigned_location_id);
    const assignedShift = shifts.find(s => s.id === hr.assigned_shift_id);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.01 }}
      >
        <Card className="bg-wt-surface border-wt-border hover:border-wt-gold/30 transition-colors">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div className="relative">
                <div className="h-14 w-14 rounded-full bg-gradient-to-br from-wt-gold to-wt-gold-hover flex items-center justify-center">
                  {employee.profile_photo ? (
                    <img
                      src={employee.profile_photo}
                      alt={employee.full_name}
                      className="h-14 w-14 rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-xl font-bold text-black">
                      {employee.full_name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                {hasSelfie && (
                  <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-green-500 flex items-center justify-center">
                    <CheckCircle className="h-3 w-3 text-white" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-wt-text-primary truncate">
                    {employee.full_name}
                  </h3>
                  {hr.employee_code && (
                    <span className="text-xs px-2 py-0.5 rounded bg-wt-gold/10 text-wt-gold">
                      {hr.employee_code}
                    </span>
                  )}
                </div>
                <p className="text-sm text-wt-text-muted truncate">{employee.email}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {hr.department && (
                    <span className="text-xs px-2 py-1 rounded bg-blue-500/10 text-blue-400 flex items-center gap-1">
                      <Building className="h-3 w-3" />
                      {hr.department}
                    </span>
                  )}
                  {hr.designation && (
                    <span className="text-xs px-2 py-1 rounded bg-purple-500/10 text-purple-400 flex items-center gap-1">
                      <Briefcase className="h-3 w-3" />
                      {hr.designation}
                    </span>
                  )}
                  {/* Work Type Badge */}
                  <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                    workType === 'field' 
                      ? 'bg-orange-500/10 text-orange-400' 
                      : 'bg-green-500/10 text-green-400'
                  }`}>
                    {workType === 'field' ? <Truck className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                    {workType === 'field' ? 'Field' : 'Office'}
                  </span>
                </div>
                {/* Assignment info */}
                <div className="flex flex-wrap gap-3 mt-1 text-xs text-wt-text-muted">
                  {assignedLocation && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {assignedLocation.name}
                    </span>
                  )}
                  {assignedShift && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {assignedShift.name}
                    </span>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="hidden md:flex flex-col items-end gap-1">
                {hasSalary ? (
                  <span className="text-sm text-green-400 flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    {hr.monthly_salary?.toLocaleString()} {currency}
                  </span>
                ) : (
                  <span className="text-xs text-orange-400">Salary not set</span>
                )}
                <span className="text-xs text-wt-text-muted flex items-center gap-1">
                  <Award className="h-3 w-3" />
                  Streak: {hr.attendance_streak || 0} days
                </span>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-wt-border text-wt-text-secondary hover:text-wt-gold"
                  onClick={() => handleEditEmployee(employee)}
                  data-testid={`edit-employee-${employee.id}`}
                  title="Edit Employee"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-wt-border text-emerald-400 hover:text-wt-gold"
                  onClick={() => { setIncentiveSettingsEmployee(employee); setIncentiveSettingsOpen(true); }}
                  data-testid={`incentive-settings-${employee.id}`}
                  title="Incentive Settings"
                >
                  <Award className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-wt-border text-blue-400 hover:text-wt-gold"
                  onClick={() => handleViewAttendance(employee)}
                  data-testid={`view-attendance-${employee.id}`}
                  title="View Attendance Calendar"
                >
                  <CalendarIcon className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-wt-border text-green-400 hover:text-wt-gold"
                  onClick={() => handleViewLedger(employee)}
                  data-testid={`view-ledger-${employee.id}`}
                  title="View Financial Ledger"
                >
                  <Wallet className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className={`border-wt-border ${hasSelfie ? 'text-green-400' : 'text-orange-400'} hover:text-wt-gold`}
                  onClick={() => handleRegisterFace(employee)}
                  data-testid={`register-face-${employee.id}`}
                  title="Register Selfie"
                >
                  <Camera className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employee-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Employee Management</h1>
          <p className="text-wt-text-muted">{employees.length} employees</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
          <Input
            placeholder="Search by name, email, or employee code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-wt-surface border-wt-border"
            data-testid="employee-search"
          />
        </div>
        <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
          <SelectTrigger className="w-48 bg-wt-surface border-wt-border">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="all">All Departments</SelectItem>
            {departments.map((dept) => (
              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Employee List */}
      <div className="space-y-3">
        {employees.length === 0 ? (
          <Card className="bg-wt-surface border-wt-border">
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 mx-auto mb-4 text-wt-text-muted opacity-50" />
              <p className="text-wt-text-muted">No employees found</p>
            </CardContent>
          </Card>
        ) : (
          employees.map((employee) => (
            <EmployeeCard key={employee.id} employee={employee} />
          ))
        )}
      </div>

      {/* Incentive Settings Dialog */}
      <EmployeeIncentiveSettingsDialog
        employee={incentiveSettingsEmployee}
        open={incentiveSettingsOpen}
        onOpenChange={setIncentiveSettingsOpen}
      />

      {/* Edit Employee Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">
              Edit Employee: {selectedEmployee?.full_name}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="salary" className="w-full">
            <TabsList className="bg-wt-bg border border-wt-border w-full justify-start flex-wrap h-auto gap-1 p-1">
              <TabsTrigger value="salary" className="data-[state=active]:bg-wt-gold/20">
                <DollarSign className="h-4 w-4 mr-2" />
                Salary
              </TabsTrigger>
              <TabsTrigger value="allowances" className="data-[state=active]:bg-wt-gold/20">
                <Plus className="h-4 w-4 mr-2" />
                Allowances
              </TabsTrigger>
              <TabsTrigger value="employment" className="data-[state=active]:bg-wt-gold/20">
                <Briefcase className="h-4 w-4 mr-2" />
                Employment
              </TabsTrigger>
              <TabsTrigger value="attendance" className="data-[state=active]:bg-wt-gold/20">
                <Clock className="h-4 w-4 mr-2" />
                Attendance
              </TabsTrigger>
              <TabsTrigger value="bank" className="data-[state=active]:bg-wt-gold/20">
                <CreditCard className="h-4 w-4 mr-2" />
                Bank
              </TabsTrigger>
              <TabsTrigger value="leave" className="data-[state=active]:bg-wt-gold/20">
                <CalendarIcon className="h-4 w-4 mr-2" />
                Leave
              </TabsTrigger>
            </TabsList>

            {/* Salary Tab */}
            <TabsContent value="salary" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monthly Salary ({currency})</Label>
                  <Input
                    type="number"
                    value={formData.monthly_salary}
                    onChange={(e) => setFormData({...formData, monthly_salary: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="5000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Daily Salary ({currency})</Label>
                  <Input
                    type="number"
                    value={formData.daily_salary}
                    onChange={(e) => setFormData({...formData, daily_salary: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="Auto-calculated if empty"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hourly Rate ({currency})</Label>
                  <Input
                    type="number"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Overtime Multiplier</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.overtime_rate}
                    onChange={(e) => setFormData({...formData, overtime_rate: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="1.5"
                  />
                  <p className="text-xs text-wt-text-muted">Multiplier for extra hours (e.g., 1.5 = 150%)</p>
                </div>
                <div className="space-y-2">
                  <Label>Undertime Multiplier</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.undertime_rate}
                    onChange={(e) => setFormData({...formData, undertime_rate: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="1.0"
                  />
                  <p className="text-xs text-wt-text-muted">Multiplier for less hours deduction (0 = no deduction)</p>
                </div>
              </div>
            </TabsContent>

            {/* Allowances Tab */}
            <TabsContent value="allowances" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Housing Allowance ({currency})</Label>
                  <Input
                    type="number"
                    value={formData.housing_allowance}
                    onChange={(e) => setFormData({...formData, housing_allowance: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Transport Allowance ({currency})</Label>
                  <Input
                    type="number"
                    value={formData.transport_allowance}
                    onChange={(e) => setFormData({...formData, transport_allowance: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Food Allowance ({currency})</Label>
                  <Input
                    type="number"
                    value={formData.food_allowance}
                    onChange={(e) => setFormData({...formData, food_allowance: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Other Allowances ({currency})</Label>
                  <Input
                    type="number"
                    value={formData.other_allowances}
                    onChange={(e) => setFormData({...formData, other_allowances: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="0"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Employment Tab */}
            <TabsContent value="employment" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee Code</Label>
                  <Input
                    value={formData.employee_code}
                    onChange={(e) => setFormData({...formData, employee_code: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="Auto-generated if empty"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Input
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="e.g., Operations"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input
                    value={formData.designation}
                    onChange={(e) => setFormData({...formData, designation: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="e.g., Delivery Driver"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Employment Type</Label>
                  <Select
                    value={formData.employment_type}
                    onValueChange={(value) => setFormData({...formData, employment_type: value})}
                  >
                    <SelectTrigger className="bg-wt-bg border-wt-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-surface border-wt-border">
                      <SelectItem value="full_time">Full Time</SelectItem>
                      <SelectItem value="part_time">Part Time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Joining Date</Label>
                  <DateInput
                    value={formData.joining_date}
                    onChange={(e) => setFormData({...formData, joining_date: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contract End Date</Label>
                  <DateInput
                    value={formData.contract_end_date}
                    onChange={(e) => setFormData({...formData, contract_end_date: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Attendance Tab */}
            <TabsContent value="attendance" className="space-y-4 mt-4">
              <div className="p-4 rounded-lg bg-wt-bg border border-wt-border">
                <p className="text-sm text-wt-text-muted mb-4">
                  Configure how this employee marks attendance. Office workers must be within the assigned location radius.
                  Field workers can mark attendance from anywhere (GPS is captured for reference).
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-wt-text-primary">Work Type</Label>
                  <Select
                    value={formData.work_type}
                    onValueChange={(value) => setFormData({...formData, work_type: value})}
                  >
                    <SelectTrigger className="bg-wt-bg border-wt-border" data-testid="work-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-surface border-wt-border">
                      <SelectItem value="office">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-green-400" />
                          <span>Office Worker</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="field">
                        <div className="flex items-center gap-2">
                          <Truck className="h-4 w-4 text-orange-400" />
                          <span>Field Worker</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-wt-text-muted">
                    {formData.work_type === 'office' 
                      ? 'Must be within GPS radius of assigned location'
                      : 'Can mark attendance from any location (GPS captured for reference)'}
                  </p>
                </div>

                {formData.work_type === 'office' && (
                  <div className="space-y-2">
                    <Label className="text-wt-text-primary">Assigned Location</Label>
                    <Select
                      value={formData.assigned_location_id || 'none'}
                      onValueChange={(value) => setFormData({...formData, assigned_location_id: value === 'none' ? '' : value})}
                    >
                      <SelectTrigger className="bg-wt-bg border-wt-border" data-testid="location-select">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent className="bg-wt-surface border-wt-border">
                        <SelectItem value="none">
                          <span className="text-wt-text-muted">Use default location</span>
                        </SelectItem>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-wt-gold" />
                              <span>{loc.name}</span>
                              <span className="text-xs text-wt-text-muted">({loc.allowed_radius_meters}m radius)</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {locations.length === 0 && (
                      <p className="text-xs text-orange-400">
                        No locations configured. Add locations in HR Settings → Locations.
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-wt-text-primary">Assigned Shift</Label>
                  <Select
                    value={formData.assigned_shift_id || 'none'}
                    onValueChange={(value) => setFormData({...formData, assigned_shift_id: value === 'none' ? '' : value})}
                  >
                    <SelectTrigger className="bg-wt-bg border-wt-border" data-testid="shift-select">
                      <SelectValue placeholder="Select shift" />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-surface border-wt-border">
                      <SelectItem value="none">
                        <span className="text-wt-text-muted">Use default shift</span>
                      </SelectItem>
                      {shifts.map((shift) => (
                        <SelectItem key={shift.id} value={shift.id}>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-blue-400" />
                            <span>{shift.name}</span>
                            <span className="text-xs text-wt-text-muted">
                              ({shift.start_time} - {shift.end_time})
                            </span>
                            {shift.is_default && (
                              <span className="text-xs px-1 bg-wt-gold/20 text-wt-gold rounded">Default</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {shifts.length === 0 && (
                    <p className="text-xs text-orange-400">
                      No shifts configured. Add shifts in HR Settings → Shifts.
                    </p>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Bank Tab */}
            <TabsContent value="bank" className="space-y-4 mt-4">
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input
                    value={formData.bank_name}
                    onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="e.g., Emirates NBD"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Account Number</Label>
                  <Input
                    value={formData.bank_account_number}
                    onChange={(e) => setFormData({...formData, bank_account_number: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="Account number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>IBAN</Label>
                  <Input
                    value={formData.iban}
                    onChange={(e) => setFormData({...formData, iban: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="AE..."
                  />
                </div>
              </div>
            </TabsContent>

            {/* Leave Tab */}
            <TabsContent value="leave" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Paid Leave Balance (days)</Label>
                  <Input
                    type="number"
                    value={formData.paid_leave_balance}
                    onChange={(e) => setFormData({...formData, paid_leave_balance: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sick Leave Balance (days)</Label>
                  <Input
                    type="number"
                    value={formData.sick_leave_balance}
                    onChange={(e) => setFormData({...formData, sick_leave_balance: e.target.value})}
                    className="bg-wt-bg border-wt-border"
                    placeholder="10"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="border-wt-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEmployee}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Selfie Capture Dialog */}
      <SelfieCapture
        open={faceDialogOpen}
        onOpenChange={setFaceDialogOpen}
        userId={selectedEmployee?.id}
        userName={selectedEmployee?.full_name}
        onSuccess={fetchEmployees}
        mode="register"
      />

      {/* Attendance Calendar Dialog */}
      <Dialog open={calendarDialogOpen} onOpenChange={setCalendarDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl flex items-center gap-3">
              <CalendarIcon className="h-6 w-6 text-wt-gold" />
              {calendarEmployee?.full_name}'s Attendance
            </DialogTitle>
          </DialogHeader>
          
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevMonth}
              className="border-wt-border"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h3 className="text-lg font-semibold text-wt-text-primary">
              {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextMonth}
              className="border-wt-border"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mb-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-500"></div>
              <span className="text-wt-text-secondary">Present</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-500"></div>
              <span className="text-wt-text-secondary">Late</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-orange-500"></div>
              <span className="text-wt-text-secondary">Half Day</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-500"></div>
              <span className="text-wt-text-secondary">Absent</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-blue-500"></div>
              <span className="text-wt-text-secondary">Leave</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-purple-500"></div>
              <span className="text-wt-text-secondary">Holiday</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gray-500"></div>
              <span className="text-wt-text-secondary">Week Off</span>
            </div>
          </div>

          {/* Calendar Grid */}
          {loadingAttendance ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center py-2 text-wt-text-muted text-sm font-medium">
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {(() => {
                const year = calendarMonth.getFullYear();
                const month = calendarMonth.getMonth();
                const firstDay = new Date(year, month, 1).getDay();
                const daysInMonth = new Date(year, month + 1, 0).getDate();
                const days = [];
                
                // Get employee's shift to determine working days
                const employeeShift = calendarEmployee?.hr_data?.assigned_shift_id 
                  ? shifts.find(s => s.id === calendarEmployee.hr_data.assigned_shift_id)
                  : shifts.find(s => s.is_default);
                  
                // working_days uses 0=Monday, 6=Sunday in backend but JS getDay() uses 0=Sunday
                // Convert: JS day 0 (Sun) = backend day 6, JS day 1 (Mon) = backend day 0, etc.
                const jsToBackendDay = (jsDay) => jsDay === 0 ? 6 : jsDay - 1;
                const workingDays = employeeShift?.working_days || [0, 1, 2, 3, 4, 5]; // Default Mon-Sat
                
                // Empty cells for days before the first of the month
                for (let i = 0; i < firstDay; i++) {
                  days.push(<div key={`empty-${i}`} className="h-20"></div>);
                }
                
                // Days of the month
                for (let day = 1; day <= daysInMonth; day++) {
                  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const record = attendanceRecords.find(r => r.date === dateStr);
                  const jsDay = new Date(year, month, day).getDay();
                  const backendDay = jsToBackendDay(jsDay);
                  const isWeekOff = !workingDays.includes(backendDay);
                  const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
                  const isFuture = new Date(year, month, day) > new Date();
                  
                  // Check if this day is a holiday
                  const holiday = calendarHolidays.find(h => h.date === dateStr);
                  const isHoliday = !!holiday;
                  
                  let bgColor = 'bg-wt-bg';
                  let statusText = '';
                  let checkInTime = '';
                  let checkOutTime = '';
                  
                  // Priority: Holiday > Attendance Record > Week Off
                  if (isHoliday && !record) {
                    bgColor = 'bg-purple-500/20 border-purple-500';
                    statusText = holiday.name || 'Holiday';
                  } else if (record) {
                    if (record.status === 'present' || record.status === 'on_time') {
                      bgColor = 'bg-green-500/20 border-green-500';
                      statusText = 'Present';
                    } else if (record.status === 'late') {
                      bgColor = 'bg-yellow-500/20 border-yellow-500';
                      statusText = 'Late';
                    } else if (record.status === 'half_day') {
                      bgColor = 'bg-orange-500/20 border-orange-500';
                      statusText = 'Half Day';
                    } else if (record.status === 'absent') {
                      bgColor = 'bg-red-500/20 border-red-500';
                      statusText = 'Absent';
                    } else if (record.status === 'on_leave') {
                      bgColor = 'bg-blue-500/20 border-blue-500';
                      statusText = 'Leave';
                    } else if (record.status === 'week_off' || record.status === 'holiday') {
                      bgColor = isHoliday ? 'bg-purple-500/20 border-purple-500' : 'bg-gray-500/10';
                      statusText = isHoliday ? (holiday.name || 'Holiday') : 'Week Off';
                    }
                    
                    // Only show times for statuses that have them
                    if (!['absent', 'on_leave', 'week_off'].includes(record.status)) {
                      if (record.check_in_time) {
                        checkInTime = formatTimeInCompanyTz(record.check_in_time);
                      }
                      if (record.check_out_time) {
                        checkOutTime = formatTimeInCompanyTz(record.check_out_time);
                      }
                    }
                  } else if (isWeekOff) {
                    bgColor = 'bg-gray-500/10';
                    statusText = 'Week Off';
                  } else if (!isFuture && !isToday) {
                    bgColor = 'bg-red-500/10';
                    statusText = 'No Record';
                  }
                  
                  days.push(
                    <div 
                      key={day}
                      data-testid={`day-cell-${day}`}
                      className={`h-20 p-1 rounded border ${bgColor} ${isToday ? 'ring-2 ring-wt-gold' : 'border-wt-border'} ${isFuture ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-wt-gold/10 transition-colors'} overflow-hidden`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isFuture) handleDayClick(day, record);
                      }}
                      title={isFuture ? 'Cannot edit future dates' : (record?.notes ? `Notes: ${record.notes}` : 'Click to edit attendance')}
                    >
                      <div className="text-sm font-medium text-wt-text-primary">{day}</div>
                      {statusText && (
                        <div className="text-xs text-wt-text-secondary">{statusText}</div>
                      )}
                      {checkInTime && (
                        <div className="text-xs text-green-400">In: {checkInTime}</div>
                      )}
                      {checkOutTime && (
                        <div className="text-xs text-blue-400">Out: {checkOutTime}</div>
                      )}
                      {record?.notes && (
                        <div className="text-xs text-yellow-400 truncate flex items-center gap-1" title={`Notes: ${record.notes}`}>
                          <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <span className="truncate">{record.notes}</span>
                        </div>
                      )}
                    </div>
                  );
                }
                
                return days;
              })()}
            </div>
          )}

          {/* Monthly Summary */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-green-500/10 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-400">
                {attendanceRecords.filter(r => ['present', 'on_time', 'late'].includes(r.status)).length}
              </div>
              <div className="text-sm text-wt-text-muted">Present Days</div>
            </div>
            <div className="bg-yellow-500/10 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-yellow-400">
                {attendanceRecords.filter(r => r.status === 'late').length}
              </div>
              <div className="text-sm text-wt-text-muted">Late Days</div>
            </div>
            <div className="bg-red-500/10 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-red-400">
                {attendanceRecords.filter(r => r.status === 'absent').length}
              </div>
              <div className="text-sm text-wt-text-muted">Absent Days</div>
            </div>
            <div className="bg-blue-500/10 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-400">
                {attendanceRecords.filter(r => r.status === 'on_leave').length}
              </div>
              <div className="text-sm text-wt-text-muted">Leave Days</div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Attendance Dialog */}
      <Dialog open={editAttendanceDialogOpen} onOpenChange={setEditAttendanceDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-xl flex items-center gap-3">
              <Clock className="h-5 w-5 text-wt-gold" />
              Edit Attendance - {selectedDate}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Status Selection */}
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Attendance Status</Label>
              <Select
                value={attendanceFormData.status}
                onValueChange={(value) => setAttendanceFormData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="bg-wt-surface border-wt-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-surface border-wt-border">
                  <SelectItem value="present">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      Present
                    </span>
                  </SelectItem>
                  <SelectItem value="late">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      Late
                    </span>
                  </SelectItem>
                  <SelectItem value="half_day">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                      Half Day
                    </span>
                  </SelectItem>
                  <SelectItem value="absent">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      Absent
                    </span>
                  </SelectItem>
                  <SelectItem value="on_leave">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      On Leave
                    </span>
                  </SelectItem>
                  <SelectItem value="week_off">
                    <span className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                      Week Off
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Check-in Time - Only show for statuses that need time */}
            {!['absent', 'on_leave', 'week_off'].includes(attendanceFormData.status) && (
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Check-in Time</Label>
                <TimeInput
                  value={attendanceFormData.check_in_time}
                  onChange={(e) => setAttendanceFormData(prev => ({ ...prev, check_in_time: e.target.value }))}
                  className="bg-wt-surface border-wt-border text-wt-text-primary [color-scheme:dark]"
                />
              </div>
            )}

            {/* Check-out Time - Only show for statuses that need time */}
            {!['absent', 'on_leave', 'week_off'].includes(attendanceFormData.status) && (
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Check-out Time</Label>
                <TimeInput
                  value={attendanceFormData.check_out_time}
                  onChange={(e) => setAttendanceFormData(prev => ({ ...prev, check_out_time: e.target.value }))}
                  className="bg-wt-surface border-wt-border text-wt-text-primary [color-scheme:dark]"
                />
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Notes (Optional)</Label>
              <Textarea
                placeholder="Reason for manual entry..."
                value={attendanceFormData.notes}
                onChange={(e) => setAttendanceFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-wt-surface border-wt-border text-wt-text-primary placeholder:text-wt-text-muted"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="destructive"
              onClick={handleDeleteAttendance}
              disabled={savingAttendance}
              className="mr-auto"
            >
              Delete Record
            </Button>
            <Button
              variant="outline"
              onClick={() => setEditAttendanceDialogOpen(false)}
              className="border-wt-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAttendance}
              disabled={savingAttendance}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black"
            >
              {savingAttendance ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Attendance'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Ledger Dialog */}
      <Dialog open={ledgerDialogOpen} onOpenChange={setLedgerDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl flex items-center gap-3">
              <Wallet className="h-6 w-6 text-wt-gold" />
              {ledgerEmployee?.full_name}'s Financial Ledger
            </DialogTitle>
          </DialogHeader>
          
          {loadingLedger ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary Cards */}
              {ledgerEntries.length > 0 && (() => {
                const summary = calculateLedgerSummary();
                return (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-green-500/10 rounded-xl p-4 text-center">
                    <TrendingUp className="h-6 w-6 text-green-400 mx-auto mb-2" />
                    <p className="text-xs text-wt-text-muted">Total Earned</p>
                    <p className="text-xl font-bold text-green-400">
                      {summary.total_credits?.toLocaleString()} {currency}
                    </p>
                  </div>
                  <div className="bg-red-500/10 rounded-xl p-4 text-center">
                    <TrendingDown className="h-6 w-6 text-red-400 mx-auto mb-2" />
                    <p className="text-xs text-wt-text-muted">Total Deductions</p>
                    <p className="text-xl font-bold text-red-400">
                      {summary.total_debits?.toLocaleString()} {currency}
                    </p>
                  </div>
                  <div className={`${summary.net_balance >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'} rounded-xl p-4 text-center`}>
                    <Wallet className={`h-6 w-6 ${summary.net_balance >= 0 ? 'text-green-400' : 'text-red-400'} mx-auto mb-2`} />
                    <p className="text-xs text-wt-text-muted">Net Balance</p>
                    <p className={`text-xl font-bold ${summary.net_balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {summary.net_balance?.toLocaleString()} {currency}
                    </p>
                  </div>
                </div>
                );
              })()}

              {/* Filter and Actions */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Filter className="h-4 w-4 text-wt-text-muted" />
                  <Select value={ledgerFilter} onValueChange={setLedgerFilter}>
                    <SelectTrigger className="w-[160px] bg-wt-bg border-wt-border text-wt-text-primary" data-testid="ledger-type-filter">
                      <SelectValue placeholder="Filter by type" />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-paper border-wt-border">
                      <SelectItem value="all">All Transactions</SelectItem>
                      <SelectItem value="salary">Salary</SelectItem>
                      <SelectItem value="incentive">Incentive</SelectItem>
                      <SelectItem value="bonus">Bonus</SelectItem>
                      <SelectItem value="advance">Advance</SelectItem>
                      <SelectItem value="penalty">Penalty</SelectItem>
                      <SelectItem value="leave_deduction">Leave Deduction</SelectItem>
                    </SelectContent>
                  </Select>
                  <DateInput
                    value={ledgerDateRange.start}
                    onChange={(e) => handleLedgerDateChange('start', e.target.value)}
                    className="w-[150px] bg-wt-bg border-wt-border text-wt-text-primary"
                    data-testid="ledger-start-date"
                  />
                  <span className="text-wt-text-muted text-sm">to</span>
                  <DateInput
                    value={ledgerDateRange.end}
                    onChange={(e) => handleLedgerDateChange('end', e.target.value)}
                    className="w-[150px] bg-wt-bg border-wt-border text-wt-text-primary"
                    data-testid="ledger-end-date"
                  />
                  {(ledgerDateRange.start || ledgerDateRange.end) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setLedgerDateRange({ start: '', end: '' }); fetchEmployeeLedger(ledgerEmployee.id, { start: '', end: '' }); }}
                      className="text-wt-text-muted hover:text-wt-gold"
                      data-testid="ledger-clear-dates"
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <Button
                  onClick={() => setAddAdjustmentDialogOpen(true)}
                  className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Adjustment
                </Button>
              </div>

              {/* Ledger Table */}
              <div className="border border-wt-border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-wt-subtle">
                    <tr>
                      <th className="text-left p-3 text-wt-text-muted text-sm">Date</th>
                      <th className="text-left p-3 text-wt-text-muted text-sm">Type</th>
                      <th className="text-left p-3 text-wt-text-muted text-sm">Description</th>
                      <th className="text-right p-3 text-wt-text-muted text-sm">Credit</th>
                      <th className="text-right p-3 text-wt-text-muted text-sm">Debit</th>
                      <th className="text-right p-3 text-wt-text-muted text-sm">Balance</th>
                      <th className="text-center p-3 text-wt-text-muted text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredLedgerEntries().length > 0 ? (
                      getFilteredLedgerEntries().map((entry, index) => {
                        const style = getLedgerEntryStyle(entry.entry_type);
                        const isCredit = entry.amount > 0;
                        const isCancelled = entry.is_cancelled;
                        return (
                          <tr key={entry.id || index} className={`border-t border-wt-border/50 hover:bg-wt-subtle/30 ${isCancelled ? 'opacity-60' : ''}`}>
                            <td className={`p-3 text-wt-text-primary text-sm ${isCancelled ? 'line-through' : ''}`}>
                              {new Date(entry.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded text-xs ${style.bg} ${style.text} ${isCancelled ? 'line-through' : ''}`}>
                                  {style.label}
                                </span>
                                {isCancelled && (
                                  <span className="px-2 py-0.5 rounded text-xs bg-gray-500/20 text-gray-400">
                                    Cancelled
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className={`p-3 text-wt-text-primary text-sm max-w-[200px] ${isCancelled ? 'line-through' : ''}`}>
                              <div className="truncate">{entry.description}</div>
                              {entry.reference_month && (
                                <div className="text-xs text-wt-text-muted">{entry.reference_month}</div>
                              )}
                              {entry.order_number && (
                                <div className="text-xs text-wt-gold">Order: {entry.order_number}</div>
                              )}
                            </td>
                            <td className={`p-3 text-right ${isCancelled ? 'line-through' : ''}`}>
                              {isCredit && (
                                <span className={`font-semibold ${isCancelled ? 'text-gray-500' : 'text-green-400'}`}>
                                  +{Math.abs(entry.amount).toLocaleString()}
                                </span>
                              )}
                            </td>
                            <td className={`p-3 text-right ${isCancelled ? 'line-through' : ''}`}>
                              {!isCredit && (
                                <span className={`font-semibold ${isCancelled ? 'text-gray-500' : 'text-red-400'}`}>
                                  -{Math.abs(entry.amount).toLocaleString()}
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-right">
                              <span className={`font-semibold ${entry.balance_after >= 0 ? 'text-wt-text-primary' : 'text-red-400'}`} data-testid={`ledger-balance-${entry.id}`}>
                                {typeof entry.balance_after === 'number' ? entry.balance_after.toLocaleString() : '—'}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              {isCancelled ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRestoreLedgerEntry(entry.id)}
                                  className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                  title="Restore this transaction"
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCancelLedgerEntry(entry.id)}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  title="Cancel this transaction"
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-wt-text-muted">
                          No ledger entries found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Adjustment Dialog */}
      <Dialog open={addAdjustmentDialogOpen} onOpenChange={setAddAdjustmentDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Add Manual Adjustment</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-wt-text-secondary">Type</Label>
              <Select 
                value={adjustmentForm.entry_type} 
                onValueChange={(v) => setAdjustmentForm({...adjustmentForm, entry_type: v})}
              >
                <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  <SelectItem value="incentive">Incentive (Credit)</SelectItem>
                  <SelectItem value="bonus">Bonus (Credit)</SelectItem>
                  <SelectItem value="payment">Payment (Debit)</SelectItem>
                  <SelectItem value="penalty">Penalty (Debit)</SelectItem>
                  <SelectItem value="leave_deduction">Leave Deduction (Debit)</SelectItem>
                  <SelectItem value="adjustment">Other Adjustment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-wt-text-secondary">Amount ({currency})</Label>
              <Input
                type="number"
                value={adjustmentForm.amount}
                onChange={(e) => setAdjustmentForm({...adjustmentForm, amount: e.target.value})}
                placeholder="Enter amount"
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              />
              <p className="text-xs text-wt-text-muted mt-1">
                {['penalty', 'leave_deduction', 'payment'].includes(adjustmentForm.entry_type) 
                  ? 'This will be recorded as a deduction' 
                  : 'This will be recorded as a credit'}
              </p>
            </div>
            
            <div>
              <Label className="text-wt-text-secondary">Description</Label>
              <Input
                value={adjustmentForm.description}
                onChange={(e) => setAdjustmentForm({...adjustmentForm, description: e.target.value})}
                placeholder="e.g., Performance bonus for Q1"
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddAdjustmentDialogOpen(false)}
              className="border-wt-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddAdjustment}
              disabled={savingAdjustment}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black"
            >
              {savingAdjustment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Add Adjustment'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeManagementPage;
